import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import './app.css';
import {
  api, ApiError, CATEGORIES, isSignedIn, setRefresh, setToken, type Home, type PayMethod, type Payment, type SignInResult,
} from './lib/api';
import { applyLang, detectLang, isLoaded, LANGUAGES, loadLang, meta, translator, type Lang, type Key, type T } from './lib/i18n';
import { daysBetween, fmtDate, money, today } from './lib/format';
import { useResource, useSession } from './lib/hooks';

/**
 * The support phone, as the BurjManage team set it in the admin console, so a
 * new number shows without rebuilding this site. VITE_SUPPORT_PHONE is only the
 * fallback, for when the platform cannot be reached or nothing is set.
 */
function useSupportPhone(): string {
  const set = useResource(() => api.support().catch(() => ({ phone: null })), []);
  return set.data?.phone || (import.meta.env.VITE_SUPPORT_PHONE ?? '');
}

/**
 * BurjManage — what a resident opens from the rent reminder.
 *
 * No app to install, works on a borrowed laptop. In shared housing
 * an app that needs installing reaches a fraction of residents: cheap phones,
 * full storage, and a reasonable reluctance to install something for a landlord.
 * A link in an SMS reaches everyone.
 *
 * Ten languages, three right-to-left. The picker is on the sign-in screen because
 * somebody who cannot read that page cannot reach a settings menu.
 */

/** refreshToken buys the next fifteen-minute access token, once. */
interface Session { accessToken: string; refreshToken?: string; tenant: { id: string; name: string } }
type Tab = 'home' | 'payments' | 'report';

function App(): React.JSX.Element {
  const [lang, setLang] = useState<Lang>(detectLang);
  const [session, setSession] = useSession<Session>('burj.tenant');
  const [tab, setTab] = useState<Tab>('home');

  const t = translator(lang);
  setToken(session?.accessToken ?? null);
  setRefresh(session?.refreshToken ?? null,
    (fresh) => setSession(session ? { ...session, ...fresh } : null));
  const signOut = (): void => {
    if (session?.refreshToken) void api.logout(session.refreshToken);
    setSession(null);
  };

  // a language other than English is its own file, fetched the first time it is chosen
  const [, loadedLang] = useState(0);
  useEffect(() => {
    applyLang(lang);
    let live = true;
    void loadLang(lang).then(() => { if (live) loadedLang((n) => n + 1); });
    return () => { live = false; };
  }, [lang]);
  useEffect(() => {
    const out = (): void => setSession(null);
    window.addEventListener('burj:signed-out', out);
    return () => window.removeEventListener('burj:signed-out', out);
  }, [setSession]);

  if (!isLoaded(lang)) return <div className="spinner" />;

  if (!session) {
    return <SignIn t={t} lang={lang} onLang={setLang} onSignedIn={setSession} />;
  }

  return (
    <>
      <header className="top">
        <div className="in">
          <span className="wm">Burj<em>Manage</em></span>
          <div className="r">
            <LanguageButton lang={lang} onLang={setLang} t={t} />
            <button className="signout" onClick={signOut}>{t('nav.signout')}</button>
          </div>
        </div>
      </header>

      <main className="shell">
        {tab === 'home' && <HomeScreen t={t} lang={lang} name={session.tenant.name} onReport={() => setTab('report')} />}
        {tab === 'payments' && <PaymentsScreen t={t} lang={lang} />}
        {tab === 'report' && <ReportScreen t={t} lang={lang} onDone={() => setTab('home')} />}
      </main>

      <nav className="tabbar">
        {(['home', 'payments', 'report'] as const).map((key) => (
          <button key={key} className={tab === key ? 'on' : ''} onClick={() => setTab(key)}>
            {t(`nav.${key}` as Key)}
          </button>
        ))}
      </nav>
    </>
  );
}

/* --------------------------------------------------------------- sign in */

/*
 * Signing in, and the two roads that end in it.
 *
 *   sign in      email + password, or Google
 *   create       the email (a code to it), the phone (a code by SMS), a password
 *   forgot       the same road as create: the email, then a new password
 *
 * The platform says what is still to prove after each step (`next`), so a
 * resident whose office changed their number is asked for a code to the new one
 * whichever way they came in. This screen follows; it decides nothing.
 */
type Stage = 'signin' | 'email' | 'emailCode' | 'phone' | 'phoneCode' | 'password';

function SignIn({ t, lang, onLang, onSignedIn }: {
  t: T; lang: Lang; onLang: (l: Lang) => void; onSignedIn: (s: Session) => void;
}): React.JSX.Element {
  const [stage, setStage] = useState<Stage>('signin');
  const [resetting, setResetting] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [shown, setShown] = useState(false);
  const [code, setCode] = useState('');
  const [ticket, setTicket] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState<Key | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Key | null>(null);
  const support = useSupportPhone();
  const options = useResource(() => api.signinOptions().catch(() => null), []);
  const min = options.data?.minPasswordLength ?? 8;

  const go = (next: Stage): void => { setStage(next); setCode(''); setError(null); setBusy(false); };
  const fail = (e: unknown): void => {
    setError(`error.${e instanceof ApiError ? e.kind : 'generic'}` as Key);
    setBusy(false);
  };
  /** Signed in, or on to whatever the platform says is next. */
  const follow = (r: SignInResult): void => {
    if (isSignedIn(r)) { onSignedIn(r); return; }
    setTicket(r.ticket);
    setPhone(r.phone);
    if (r.next === 'email') { setNote('signup.confirmEmail'); go('email'); }
    else go(r.next);
  };
  const run = async (fn: () => Promise<void>): Promise<void> => {
    setBusy(true); setError(null);
    try { await fn(); setBusy(false); } catch (e) { fail(e); }
  };

  const startRoad = (reset: boolean): void => { setResetting(reset); setNote(null); setPassword(''); go('email'); };
  const sendEmail = (): Promise<void> => run(async () => { await api.emailRequest(email, lang); go('emailCode'); });
  const sendSms = (): Promise<void> => run(async () => { setPhone((await api.phoneRequest(ticket, lang)).phone); go('phoneCode'); });

  const roadTitle = resetting ? t('signup.resetTitle') : t('signup.title');
  const stepOf: Partial<Record<Stage, number>> = { email: 1, emailCode: 1, phone: 2, phoneCode: 2, password: 3 };
  const codeInput = (
    <input id="code" className="codeinput" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
  );

  return (
    <div className="gate"><div className="gatebox">
      <div className="brand">
        <div className="n">Burj<em>Manage</em></div>
        <div className="t">{t('app.tagline')}</div>
      </div>

      {/* Before anything else. Somebody who cannot read this page cannot navigate away from it. */}
      <div className="lab" style={{ marginBottom: 8 }}>{t('signin.language')}</div>
      <div className="langs">
        {LANGUAGES.map((l) => (
          <button key={l.code} className={lang === l.code ? 'on' : ''} onClick={() => onLang(l.code)}
            lang={l.code} dir={l.dir}>
            {l.label}
            {l.code !== 'en' && <small>{l.english}</small>}
          </button>
        ))}
      </div>

      <div className="card">
        {stage === 'signin' ? (
          <>
            <h2>{t('signin.title')}</h2>
            {error && <div className="note bad">{t(error)}</div>}
            <p className="hint" style={{ color: 'var(--muted)', fontSize: 15, marginTop: 0 }}>{t('signin.intro')}</p>
            <form onSubmit={(e) => { e.preventDefault(); void run(async () => follow(await api.login(email, password, lang))); }}>
              <div className="field">
                <label htmlFor="email">{t('signin.email')}</label>
                <input id="email" type="email" inputMode="email" autoComplete="username" dir="ltr"
                  value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div className="field">
                <label htmlFor="password">{t('signin.password')}</label>
                <input id="password" type={shown ? 'text' : 'password'} autoComplete="current-password" dir="ltr"
                  value={password} onChange={(e) => setPassword(e.target.value)} />
                <button type="button" className="linkbtn" onClick={() => setShown(!shown)}>
                  {shown ? t('signup.hide') : t('signup.show')}
                </button>
              </div>
              <button className="btn accent" type="submit" disabled={busy || !email.includes('@') || !password}>
                {busy ? t('signin.checking') : t('signin.submit')}
              </button>
            </form>
            <button className="btn quiet" onClick={() => startRoad(true)}>{t('signin.forgot')}</button>

            {options.data?.google?.web && (
              <>
                <div className="or"><span>{t('signin.or')}</span></div>
                <GoogleButton clientId={options.data.google.web} lang={lang}
                  onToken={(idToken) => void run(async () => follow(await api.google(idToken, lang)))}
                  onFail={() => setError('error.googleFailed')} />
              </>
            )}

            <div className="firsttime">
              <div className="hint">{t('signin.firstTime')}</div>
              <button className="btn" onClick={() => startRoad(false)}>{t('signin.create')}</button>
            </div>
          </>
        ) : (
          <>
            <h2>{stage === 'phone' || stage === 'phoneCode' ? t('signup.phoneTitle')
              : stage === 'password' ? t('signup.passwordTitle') : roadTitle}</h2>
            <div className="hint" style={{ marginBottom: 10 }}>{t('signup.step', { n: stepOf[stage] ?? 1 })}</div>
            {error && <div className="note bad">{t(error)}</div>}
            {note && stage === 'email' && <div className="note info">{t(note)}</div>}

            {stage === 'email' && (
              <form onSubmit={(e) => { e.preventDefault(); void sendEmail(); }}>
                <p className="hint" style={{ color: 'var(--muted)', fontSize: 15, marginTop: 0 }}>{t('signup.intro')}</p>
                <div className="field">
                  <label htmlFor="email">{t('signin.email')}</label>
                  <input id="email" type="email" inputMode="email" autoComplete="email" dir="ltr"
                    value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button className="btn accent" type="submit" disabled={busy || !email.includes('@')}>
                  {busy ? t('signup.sending') : t('signup.sendEmail')}
                </button>
              </form>
            )}

            {stage === 'emailCode' && (
              <form onSubmit={(e) => { e.preventDefault(); void run(async () => follow(await api.emailVerify(email, code, lang))); }}>
                <div className="note info">{t('signup.emailSent', { email: email.trim() })}</div>
                <div className="field">
                  <label htmlFor="code">{t('signup.emailCode')}</label>
                  {codeInput}
                </div>
                <button className="btn accent" type="submit" disabled={busy || code.length !== 6}>
                  {busy ? t('signup.checking') : t('signup.continue')}
                </button>
                <button type="button" className="btn quiet" disabled={busy} onClick={() => void sendEmail()}>{t('signup.resend')}</button>
              </form>
            )}

            {stage === 'phone' && (
              <>
                <p className="hint" style={{ color: 'var(--muted)', fontSize: 15, marginTop: 0 }}>
                  {t('signup.phoneIntro', { phone })}
                </p>
                <button className="btn accent" disabled={busy} onClick={() => void sendSms()}>
                  {busy ? t('signup.sending') : t('signup.sendSms')}
                </button>
                <div className="hint" style={{ marginTop: 12 }}>{t('signup.wrongPhone')}</div>
              </>
            )}

            {stage === 'phoneCode' && (
              <form onSubmit={(e) => { e.preventDefault(); void run(async () => follow(await api.phoneVerify(ticket, code, lang))); }}>
                <div className="note info">{t('signup.smsSent', { phone })}</div>
                <div className="field">
                  <label htmlFor="code">{t('signup.smsCode')}</label>
                  {codeInput}
                </div>
                <button className="btn accent" type="submit" disabled={busy || code.length !== 6}>
                  {busy ? t('signup.checking') : t('signup.continue')}
                </button>
                <button type="button" className="btn quiet" disabled={busy} onClick={() => void sendSms()}>{t('signup.resend')}</button>
              </form>
            )}

            {stage === 'password' && (
              <form onSubmit={(e) => {
                e.preventDefault();
                if (password !== repeat) { setError('signup.mismatch'); return; }
                void run(async () => follow(await api.setPassword(ticket, password, lang)));
              }}>
                <p className="hint" style={{ color: 'var(--muted)', fontSize: 15, marginTop: 0 }}>{t('signup.passwordIntro', { min })}</p>
                {/* the email as a username field, so the phone's password manager files it under the right account */}
                <input type="email" autoComplete="username" value={email} readOnly hidden />
                <div className="field">
                  <label htmlFor="newpw">{t('signup.newPassword')}</label>
                  <input id="newpw" type={shown ? 'text' : 'password'} autoComplete="new-password" dir="ltr"
                    value={password} onChange={(e) => setPassword(e.target.value)} />
                  <button type="button" className="linkbtn" onClick={() => setShown(!shown)}>
                    {shown ? t('signup.hide') : t('signup.show')}
                  </button>
                </div>
                <div className="field">
                  <label htmlFor="repeat">{t('signup.repeat')}</label>
                  <input id="repeat" type={shown ? 'text' : 'password'} autoComplete="new-password" dir="ltr"
                    value={repeat} onChange={(e) => setRepeat(e.target.value)} />
                </div>
                <button className="btn accent" type="submit" disabled={busy || password.length < min || !repeat}>
                  {busy ? t('signup.checking') : t('signup.save')}
                </button>
              </form>
            )}

            <button className="btn quiet" onClick={() => { setPassword(''); setRepeat(''); go('signin'); }}>{t('signup.backToSignin')}</button>
          </>
        )}
      </div>

      {support && <div className="hint" style={{ textAlign: 'center' }}>{t('signin.trouble', { phone: support })}</div>}
    </div></div>
  );
}

/* Google's own button (Google Identity Services): it is drawn by Google, in the
 * page's language, and hands back an ID token the platform checks itself. The
 * script is fetched only when Google sign-in is switched on. */
declare global {
  interface Window {
    google?: { accounts: { id: {
      initialize(o: { client_id: string; callback: (r: { credential: string }) => void; ux_mode?: string }): void;
      renderButton(el: HTMLElement, o: Record<string, unknown>): void;
    } } };
  }
}
let gsi: Promise<void> | null = null;
const loadGsi = (): Promise<void> => (gsi ??= new Promise((resolve, reject) => {
  const s = document.createElement('script');
  s.src = 'https://accounts.google.com/gsi/client';
  s.async = true;
  s.onload = () => resolve();
  s.onerror = () => { gsi = null; reject(new Error('gsi')); };
  document.head.appendChild(s);
}));

function GoogleButton({ clientId, lang, onToken, onFail }: {
  clientId: string; lang: Lang; onToken: (idToken: string) => void; onFail: () => void;
}): React.JSX.Element {
  const box = React.useRef<HTMLDivElement>(null);
  const latest = React.useRef(onToken);
  latest.current = onToken;
  useEffect(() => {
    let live = true;
    loadGsi().then(() => {
      if (!live || !box.current || !window.google) return;
      window.google.accounts.id.initialize({ client_id: clientId, callback: (r) => latest.current(r.credential) });
      box.current.innerHTML = '';
      window.google.accounts.id.renderButton(box.current, {
        theme: 'outline', size: 'large', text: 'continue_with', shape: 'rectangular',
        width: Math.min(360, box.current.clientWidth || 320), locale: lang === 'zh' ? 'zh-CN' : lang,
      });
    }).catch(() => { if (live) onFail(); });
    return () => { live = false; };
  }, [clientId, lang, onFail]);
  return <div ref={box} className="googlebtn" />;
}

function LanguageButton({ lang, onLang, t }: { lang: Lang; onLang: (l: Lang) => void; t: T }): React.JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button className="langbtn" onClick={() => setOpen(true)}>{meta(lang).label}</button>
      {open && (
        <div className="sheet" onClick={() => setOpen(false)}>
          <div className="in" onClick={(e) => e.stopPropagation()}>
            <h2 style={{ marginBottom: 14 }}>{t('signin.language')}</h2>
            <div className="langs">
              {LANGUAGES.map((l) => (
                <button key={l.code} className={lang === l.code ? 'on' : ''} lang={l.code} dir={l.dir}
                  onClick={() => { onLang(l.code); setOpen(false); }}>
                  {l.label}
                  {l.code !== 'en' && <small>{l.english}</small>}
                </button>
              ))}
            </div>
            <button className="btn quiet" onClick={() => setOpen(false)}>{t('common.close')}</button>
          </div>
        </div>
      )}
    </>
  );
}

/* ------------------------------------------------------------------ home */

function HomeScreen({ t, lang, name, onReport }: {
  t: T; lang: Lang; name: string; onReport: () => void;
}): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.home(), []);
  const [paying, setPaying] = useState(false);
  const support = useSupportPhone();
  if (loading) return <div className="spinner" />;
  if (error) return <Problem t={t} error={error} onRetry={reload} />;

  const home = data as Home;
  const { lease, nextRent } = home;
  const late = nextRent ? daysBetween(nextRent.due_date, today()) : 0;
  const endsIn = daysBetween(today(), lease.endDate);

  return (
    <>
      <h1 style={{ fontSize: 22, margin: '20px 0 14px' }}>{t('home.greeting', { name: name.split(' ')[0] ?? name })}</h1>

      {lease.rentPaused && <div className="note ok">{t('home.rentPaused')}</div>}

      {nextRent && !lease.rentPaused && (
        <div className="card">
          <div className="lab">{t('home.rentDue')}</div>
          <div className={`big ${late > 0 ? 'hot' : ''}`}>{money(Number(nextRent.amount))}</div>
          <div>
            {late > 0
              ? <span className="tag t-bad">{t('home.overdue', { days: late })}</span>
              : late === 0
                ? <span className="tag t-warn">{t('home.dueToday')}</span>
                : <span className="tag t-mute">{t('home.dueOn', { date: fmtDate(nextRent.due_date, lang) })}</span>}
          </div>
          {nextRent.reported_paid
            ? <div className="note ok" style={{ marginTop: 14, marginBottom: 0 }}>{t('home.reported')}</div>
            : <button className="btn" style={{ marginTop: 14 }} onClick={() => setPaying(true)}>{t('pay.cta')}</button>}
        </div>
      )}
      {paying && nextRent && (
        <PaySheet t={t} lang={lang} amount={Number(nextRent.amount)} onClose={() => setPaying(false)}
          onSent={async () => { setPaying(false); await reload(); }} />
      )}

      {!nextRent && !lease.rentPaused && <div className="note ok">{t('home.noRentDue')}</div>}

      <div className="card">
        <h2>{t('home.yourRoom')}</h2>
        <div className="row"><span className="k">{lease.property}</span><span className="v">{lease.room}</span></div>
        {lease.address && <div className="row"><span className="k">&nbsp;</span><span className="v" style={{ fontWeight: 400, color: 'var(--muted)', fontSize: 14 }}>{lease.address}</span></div>}
        <div className="row">
          <span className="k">{t('home.monthlyRent')}</span>
          <span className="v mono">{money(lease.monthlyRent)}</span>
        </div>
        <div className="row">
          <span className="k">{t('home.deposit')}</span>
          <span className="v mono">{money(lease.deposit)}</span>
        </div>
        <div className="row">
          <span className="k">{t('home.tenancy')}</span>
          <span className="v">{t('home.until', { date: fmtDate(lease.endDate, lang) })}</span>
        </div>
      </div>

      {endsIn <= 45 && endsIn >= 0 && (
        <div className="note warn">{t('home.endingSoon', { days: endsIn })}</div>
      )}

      <div className="card">
        <h2>{t('home.howToPay')}</h2>
        <p style={{ margin: 0, fontSize: 15, color: 'var(--slate)' }}>{t('home.howToPayBody')}</p>
      </div>

      <button className="btn dark" onClick={onReport}>{t('report.title')}</button>

      {support && (
        <div className="card">
          <h2>{t('home.contact')}</h2>
          <div className="row">
            <span className="k">{t('home.callOffice')}</span>
            <a className="v mono" href={`tel:${support.replace(/[^+\d]/g, '')}`} dir="ltr">{support}</a>
          </div>
        </div>
      )}

      {/* Shared housing: the laptop is often borrowed. Said once, where it is seen. */}
      <div className="note warn">{t('home.shared')}</div>
    </>
  );
}

/* -------------------------------------------------------------- payments */

function PaymentsScreen({ t, lang }: { t: T; lang: Lang }): React.JSX.Element {
  const { data, loading, error, reload } = useResource(() => api.home(), []);
  if (loading) return <div className="spinner" />;
  if (error) return <Problem t={t} error={error} onRetry={reload} />;
  const payments = (data as Home).payments ?? [];

  const kindKey = (kind: string): Key => {
    const k = kind.toLowerCase();
    return k === 'rent' ? 'payments.kind.rent'
      : k === 'deposit' ? 'payments.kind.deposit'
      : 'payments.kind.other';
  };

  return (
    <>
      <h1 style={{ fontSize: 22, margin: '20px 0 14px' }}>{t('payments.title')}</h1>
      <div className="note info">{t('payments.proof')}</div>

      <div className="card">
        {payments.map((p: Payment) => (
          <div className="pay" key={p.id}>
            <div>
              <div className="amt mono">{money(Number(p.amount), p.currency)}</div>
              <div className="meta">{t('payments.received', { date: fmtDate(p.received_on, lang) })}</div>
              <div className="meta">{t('payments.method', { method: p.method })}</div>
            </div>
            <span className="tag t-ok">{t(kindKey(p.kind))}</span>
          </div>
        ))}
        {payments.length === 0 && <div className="empty">{t('payments.none')}</div>}
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- pay */

const METHODS: PayMethod[] = ['BankTransfer', 'Cheque', 'CreditCard', 'Cash'];

/**
 * Telling the office you have paid. Nothing is charged — there is no payment
 * gateway — and the sheet says so first. The office checks the bank, then the
 * payment appears under Payments. Cash asks the office to collect.
 */
function PaySheet({ t, lang, amount, onClose, onSent }: {
  t: T; lang: Lang; amount: number; onClose: () => void; onSent: () => Promise<void>;
}): React.JSX.Element {
  const [method, setMethod] = useState<PayMethod>('BankTransfer');
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<Key | null>(null);
  const [error, setError] = useState<Key | null>(null);

  const send = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.pay({ method, ...(reference.trim() ? { reference: reference.trim() } : {}) }, lang);
      setDone(method === 'Cash' ? 'pay.cashSent' : 'pay.sent');
    } catch (e) {
      setError(`error.${e instanceof ApiError ? e.kind : 'generic'}` as Key);
    } finally { setBusy(false); }
  };

  return (
    <div className="sheet" onClick={onClose}>
      <div className="in" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={t('pay.title')}>
        <h2 style={{ marginTop: 0 }}>{t('pay.title')}</h2>
        {done ? (
          <>
            <div className="note ok">{t(done)}</div>
            <button className="btn dark" onClick={() => void onSent()}>{t('common.back')}</button>
          </>
        ) : (
          <>
            <div className="big">{money(amount)}</div>
            <p style={{ color: 'var(--slate)', fontSize: 15 }}>{t('pay.intro')}</p>
            {error && <div className="note bad">{t(error)}</div>}
            <div className="lab" style={{ marginBottom: 8 }}>{t('pay.how')}</div>
            <div className="cats">
              {METHODS.map((m) => (
                <button key={m} className={method === m ? 'on' : ''} onClick={() => setMethod(m)}>
                  {t(`pay.${m}` as Key)}
                </button>
              ))}
            </div>
            {method !== 'Cash' && (
              <div className="field">
                <label htmlFor="ref">{t('pay.reference')}</label>
                <input id="ref" value={reference} maxLength={80} onChange={(e) => setReference(e.target.value)} />
                <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 6 }}>{t('pay.referenceHint')}</div>
              </div>
            )}
            <button className="btn accent" disabled={busy} onClick={send}>
              {busy ? t('report.sending') : t('pay.send')}
            </button>
            <button className="btn" onClick={onClose}>{t('common.close')}</button>
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------- report */

function ReportScreen({ t, lang, onDone }: { t: T; lang: Lang; onDone: () => void }): React.JSX.Element {
  const [category, setCategory] = useState<string | null>(null);
  const [description, setDescription] = useState('');
  const [emergency, setEmergency] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<Key | null>(null);

  const send = async (): Promise<void> => {
    setBusy(true); setError(null);
    try {
      await api.report({ category: category!, description, emergency }, lang);
      setSent(true);
    } catch (e) {
      setError(`error.${e instanceof ApiError ? e.kind : 'generic'}` as Key);
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <>
        <h1 style={{ fontSize: 22, margin: '20px 0 14px' }}>{t('report.title')}</h1>
        <div className="note ok">{t('report.sent')}</div>
        <button className="btn dark" onClick={onDone}>{t('common.back')}</button>
      </>
    );
  }

  return (
    <>
      <h1 style={{ fontSize: 22, margin: '20px 0 14px' }}>{t('report.title')}</h1>
      <p style={{ color: 'var(--slate)', fontSize: 15, marginTop: 0 }}>{t('report.intro')}</p>
      {error && <div className="note bad">{t(error)}</div>}

      <div className="lab" style={{ marginBottom: 8 }}>{t('report.category')}</div>
      <div className="cats">
        {CATEGORIES.map((c) => (
          <button key={c} className={category === c ? 'on' : ''} onClick={() => setCategory(c)}>
            {t(`category.${c}` as Key)}
          </button>
        ))}
      </div>

      <div className="field">
        <label htmlFor="desc">{t('report.describe')}</label>
        <textarea id="desc" rows={4} value={description} placeholder={t('report.placeholder')}
          onChange={(e) => setDescription(e.target.value)} />
      </div>

      <button className={`check ${emergency ? 'on' : ''}`} onClick={() => setEmergency(!emergency)}>
        <span className="box">{emergency ? '✓' : ''}</span>
        <span>
          <b>{t('report.emergency')}</b>
          <span>{emergency ? t('report.emergencyOn') : t('report.emergencyHelp')}</span>
        </span>
      </button>

      {emergency && <div className="note bad">{t('report.callInstead')}</div>}

      <button className={`btn ${emergency ? 'danger' : 'accent'}`}
        disabled={!category || description.trim().length < 3 || busy} onClick={send}>
        {busy ? t('report.sending') : t('report.send')}
      </button>
    </>
  );
}

/* ----------------------------------------------------------------- error */

function Problem({ t, error, onRetry }: { t: T; error: Error; onRetry: () => void }): React.JSX.Element {
  const kind = error instanceof ApiError ? error.kind : 'generic';
  return (
    <div style={{ marginTop: 26 }}>
      <div className="note bad">{t(`error.${kind}` as Key)}</div>
      <button className="btn" onClick={onRetry}>{t('error.retry')}</button>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(<React.StrictMode><App /></React.StrictMode>);
